import React, { useState } from "react";
import { FaArrowLeft, FaArrowRight, FaFileExcel } from "react-icons/fa";
import { TableWithSummary } from "./TableWithSummary";
import { Select } from "../../../components/select";
import SLLogo from "../../../icons/srilanka.svg";
import QatarLogo from "../../../icons/qutar.svg";
import IndiaLogo from "../../../icons/india.svg";
import UsaLogo from "../../../icons/usa.svg";
import HongKongLogo from "../../../icons/hongkong.svg";
import NasdaqLogo from "../../../icons/nasdaqs.svg";
import SaudiLogo from "../../../icons/saudi.svg";
import DubaiLogo from "../../../icons/dubai.svg";
import AbudhabiLogo from "../../../icons/abudb.svg";
import KuwaitLogo from "../../../icons/kuwait.svg";
import Button from "../../../components/Button";
import { isAllowed } from "../../../components/Entitlements";
import { KEYS } from "../../../setup/entitlements";
import { createExportFunction } from "./summaryCalculations";

const exchangeDetails: any = {
  "Colombo Stock Exchange": { logo: SLLogo, shortCode: "CSE" },
  "New York Stock Exchange": { logo: UsaLogo, shortCode: "NYSE" },
  "HongKong Stock Exchange": { logo: HongKongLogo, shortCode: "HKEX" },
  "Qatar Stock Exchange": { logo: QatarLogo, shortCode: "QSE" },
  "Bombay Stock Exchange": { logo: IndiaLogo, shortCode: "BSE" },
  "National Association of Securities Dealers Automated Quotations": {
    logo: NasdaqLogo,
    shortCode: "NASDAQ",
  },
  "Saudi Stock Exchange": { logo: SaudiLogo, shortCode: "SSE" },
  "Dubai Financial Market": { logo: DubaiLogo, shortCode: "DFM" },
  "Abu Dhabi Securities Exchange": { logo: AbudhabiLogo, shortCode: "ADSX" },
  "Boursa Kuwait": { logo: KuwaitLogo, shortCode: "BK" },
  "Indian Stock Exchange": { logo: IndiaLogo, shortCode: "ISE" },
};

type FormatConfig = {
  decimal: string;
  scale: string;
};

type DataTableType = {
  data: any[];
  loading: boolean;
  roleName: string;
  allowed: string[];
  formatConfig: FormatConfig;
};

const TABLE_HEIGHT_OPTIONS = [
  { label: "Small", value: "calc(100vh - 400px)" },
  { label: "Medium", value: "calc(100vh - 120px)" },
  { label: "Large", value: "calc(100vh - 60px)" },
];

export const DataTable = (props: DataTableType) => {
  const [tableHeight, setTableHeight] = useState("calc(100vh - 400px)");

  function formatNumber(value: any, includeScale: boolean = false) {
    if (value === null || value === undefined || isNaN(value)) {
      return "-";
    }

    let scaledValue = value;
    let suffix = "";

    if (includeScale) {
      switch (props.formatConfig.scale) {
        case "thousands":
          scaledValue = value / 1000;
          suffix = "K";
          break;
        case "millions":
          scaledValue = value / 1000000;
          suffix = "M";
          break;
        case "billions":
          scaledValue = value / 1000000000;
          suffix = "B";
          break;
        default:
          scaledValue = value;
          suffix = "";
      }
    }

    // Apply decimal formatting based on formatConfig
    let decimalPlaces = 2; // default

    switch (props.formatConfig.decimal) {
      case "decimal_0":
        decimalPlaces = 1;
        break;
      case "decimal_00":
        decimalPlaces = 2;
        break;
      default:
        decimalPlaces = 0;
    }

    const formattedNumber = scaledValue.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });

    return `${formattedNumber}`;
  }

  const exportToExcel = createExportFunction(props.data, formatNumber, {
    enabled: true,
    labelColumn: "ticker_symbol",
    calculations: {
      average: [
        "eps",
        "pe_ratio",
        "price_to_book",
        "dividend_yield",
        "dividend_payout",
        "debt_equity",
        "eps_growth",
        "gross_profit_margin",
        "net_profit_margin",
        "operating_cash_margin",
        "revenues",
        "gross_profits",
        "net_profits",
        "market_cap",
      ],
      median: [
        "eps",
        "pe_ratio",
        "price_to_book",
        "dividend_yield",
        "dividend_payout",
        "debt_equity",
        "eps_growth",
        "gross_profit_margin",
        "net_profit_margin",
        "operating_cash_margin",
        "revenues",
        "gross_profits",
        "net_profits",
        "market_cap",
      ],
    },
    rowLabels: {
      average: "Average",
      median: "Median",
    },
    excludeColumns: ["company_name", "sector", "exchange", "traded_price"],
    columnFilters: {
      pe_ratio: (value: any) => {
        if (typeof value !== "number") return false;
        if (value < 0) return false;
        if (value > 50) return false;
        return true;
      },
      dividend_payout: (value: any) => {
        if (typeof value !== "number") return false;
        return value >= 0;
      },
    },
  });

  const addSorting = (column: any) => ({
    ...column,
    sorter: (a: any, b: any) => {
      let valueA = a[column.dataIndex];
      let valueB = b[column.dataIndex];

      if (valueA === "-") valueA = 0.0;
      if (valueB === "-") valueB = 0.0;

      if (valueA === null || valueA === undefined) return -1;
      if (valueB === null || valueB === undefined) return 1;

      return Number(valueA) - Number(valueB);
    },
  });

  const columns: any = [
    {
      key: "ticker_symbol",
      title: "Ticker",
      dataIndex: "ticker_symbol",
      width: 100,
      fixed: window.innerWidth >= 640 ? "left" : undefined,
    },
    {
      key: "company_name",
      title: "Company",
      dataIndex: "company_name",
      width: 200,
      fixed: window.innerWidth >= 640 ? "left" : undefined,
    },
    {
      key: "sector",
      title: "Sector",
      dataIndex: "sector",
      width: 200,
    },
    {
      key: "exchange",
      title: "Exchange",
      dataIndex: "exchange",
      width: 220,
      render: (exchange: any) => (
        <div className="flex items-center gap-2">
          <img
            src={exchangeDetails[exchange]?.logo || QatarLogo}
            alt={exchange}
            className="w-4 h-4"
          />
          <span className="text-nowrap">{exchange}</span>
        </div>
      ),
    },
    addSorting({
      key: "traded_price",
      title: "Traded Price",
      dataIndex: "traded_price",
      width: 150,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value)}
          </h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "eps",
      title: "EPS",
      dataIndex: "eps",
      width: 100,
      align: "right",
      render: (value: any, e: any) => (
        <span>
          {e.currency}{" "}
          {typeof value === "number" ? formatNumber(value) : "0.00"}
        </span>
      ),
    }),
    addSorting({
      key: "pe_ratio",
      title: "P/E Ratio",
      dataIndex: "pe_ratio",
      width: 100,
      align: "right",
      render: (value: any) => {
        let displayValue;

        if (typeof value !== "number") {
          displayValue = "NaN";
        } else if (value > 50) {
          displayValue = ">50";
        } else if (value >= 0) {
          displayValue = formatNumber(value);
        } else {
          displayValue = "NaN";
        }
        return <span>{displayValue}</span>;
      },
    }),
    addSorting({
      key: "pricesToBook",
      title: "Price to Book",
      dataIndex: "price_to_book",
      width: 120,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">{formatNumber(value)}</h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "dividendYield",
      title: "Dividend Yield",
      dataIndex: "dividend_yield",
      width: 140,
      align: "right",
      render: (value: any) =>
        value !== null && value !== undefined && !isNaN(value)
          ? `${formatNumber(value)} %`
          : "-",
    }),
    addSorting({
      key: "dividend_payout",
      title: "Dividend Payout",
      dataIndex: "dividend_payout",
      width: 140,
      align: "right",
      render: (value: any) => (
        <div className=" w-[120px]">
          {value !== null && value !== undefined && !isNaN(value)
            ? `${formatNumber(Math.max(0, value))} %`
            : "-"}
        </div>
      ),
    }),
    addSorting({
      key: "debt_equity",
      title: "Debt/Equity",
      dataIndex: "debt_equity",
      width: 120,
      align: "right",
      render: (value: any) =>
        value !== null && value !== undefined && !isNaN(value)
          ? formatNumber(value)
          : "-",
    }),
    addSorting({
      key: "eps_growth",
      title: "EPS Growth",
      dataIndex: "eps_growth",
      width: 120,
      align: "right",
      render: (value: any) =>
        value !== null && value !== undefined && !isNaN(value)
          ? `${formatNumber(value)} %`
          : "-",
    }),
    addSorting({
      key: "gross_profit_margin",
      title: "Gross Margin",
      dataIndex: "gross_profit_margin",
      width: 120,
      align: "right",
      render: (value: any) =>
        value !== null && value !== undefined && !isNaN(value)
          ? `${formatNumber(value)} %`
          : "-",
    }),
    addSorting({
      key: "net_profit_margin",
      title: "Net Profit Margin",
      dataIndex: "net_profit_margin",
      width: 120,
      align: "right",
      render: (value: any) =>
        value !== null && value !== undefined && !isNaN(value)
          ? `${formatNumber(value)} %`
          : "-",
    }),
    addSorting({
      key: "operating_cash_margin",
      title: "Operating Cash Margin",
      dataIndex: "operating_cash_margin",
      width: 150,
      align: "right",
      render: (value: any) =>
        value !== null && value !== undefined && !isNaN(value)
          ? `${formatNumber(value)} %`
          : "-",
    }),
    addSorting({
      key: "revenues",
      title: "Revenue",
      dataIndex: "revenues",
      width: 120,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value, true)}
          </h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "gross_profits",
      title: "Gross Profit",
      dataIndex: "gross_profits",
      width: 120,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value, true)}
          </h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "net_profits",
      title: "Net Profit",
      dataIndex: "net_profits",
      width: 120,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value, true)}
          </h1>
        ) : (
          "-"
        ),
    }),
    addSorting({
      key: "market_cap",
      title: "Market Capitalization",
      dataIndex: "market_cap",
      width: 120,
      align: "right",
      render: (value: any, e: any) =>
        value !== null && value !== undefined && !isNaN(value) ? (
          <h1 className="text-nowrap">
            {e.currency} {formatNumber(value, true)}
          </h1>
        ) : (
          "-"
        ),
    }),
  ];

  return (
    <div className="border rounded-lg shadow-sm vi-scr-card">
      <div className="p-2 border-b">
        <div className="flex items-center justify-end gap-2">
          {(props.roleName === "admin" ||
            isAllowed([KEYS.EXPORT_TABLE_OPTION], props.allowed)) && (
            <Button
              onClick={exportToExcel}
              // Was a flat bg-green-600 pill with no icon — Button.tsx's
              // own className prop fully replaces its variant classes
              // rather than merging with them (it lands in `...rest`,
              // spread after `className={buttonClasses}` in the JSX),
              // so this can be restyled directly without fighting
              // anything. Matches the same accent-gradient CTA
              // treatment used everywhere else in the app instead of a
              // one-off green.
              className="!text-sm !rounded-lg inline-flex w-full items-center justify-center gap-2 py-2 px-3 font-medium shadow-sm transition-opacity duration-200 hover:opacity-90 lg:w-auto"
              style={{
                background: "var(--vi-accent-grad)",
                color: "var(--vi-accent-on)",
              }}
            >
              <FaFileExcel size={14} />
              Export to Excel
            </Button>
          )}
          <span className="text-sm text-gray-600 font-dm">Table Height:</span>
          <Select
            options={TABLE_HEIGHT_OPTIONS}
            value={tableHeight}
            onChange={(value: any) => setTableHeight(value)}
            className="w-32 !font-dm"
            popupClassName="vi-scr-popup"
            id="dsd"
            placeholder="Select A Height"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <TableWithSummary
          columns={columns}
          dataSource={props?.data ? props.data : []}
          pagination={{
            total: props?.data?.length,
            position: ["bottomCenter"],
            showSizeChanger: true,
            pageSizeOptions: [50, 100, 200],
            defaultPageSize: 100,
            prevIcon: (
              <div className="flex items-center gap-x-2">
                <FaArrowLeft className="text-[#667085]" />
                <h3 className="text-[#667085]">Previous</h3>
              </div>
            ),
            nextIcon: (
              <div className="flex items-center gap-x-2">
                <h3 className="text-[#667085]">Next</h3>
                <FaArrowRight className="text-[#667085]" />
              </div>
            ),
          }}
          scroll={{ x: "max-content", y: tableHeight }}
          rowKey="srNo"
          className="w-full"
          loading={props.loading}
          summary={{
            enabled: true,
            sticky: true,
            labelColumn: "ticker_symbol",
            calculations: {
              average: [
                "eps",
                "pe_ratio",
                "price_to_book",
                "dividend_yield",
                "dividend_payout",
                "debt_equity",
                "eps_growth",
                "gross_profit_margin",
                "net_profit_margin",
                "operating_cash_margin",
                "revenues",
                "gross_profits",
                "net_profits",
                "market_cap",
              ],
              median: [
                "eps",
                "pe_ratio",
                "price_to_book",
                "dividend_yield",
                "dividend_payout",
                "debt_equity",
                "eps_growth",
                "gross_profit_margin",
                "net_profit_margin",
                "operating_cash_margin",
                "revenues",
                "gross_profits",
                "net_profits",
                "market_cap",
              ],
            },
            rowLabels: {
              average: "Average",
              median: "Median",
            },
            excludeColumns: [
              "company_name",
              "sector",
              "exchange",
              "traded_price",
            ],
            columnFilters: {
              pe_ratio: (value: any) => {
                if (typeof value !== "number") return false;
                if (value < 0) return false;
                if (value > 50) return false;
                return true;
              },
              dividend_payout: (value: any) => {
                if (typeof value !== "number") return false;
                return value >= 0;
              },
            },
          }}
        />
      </div>
    </div>
  );
};
